import { filterApplicableOverweightPolicies } from "@/lib/ratePolicies";
import type { OverweightPolicy } from "@/types/api";
import type { RateLineRow } from "@/types/rate";

/** Shared reddish haze + fine border for a rate line over the weight cap. */
export const RATE_LINE_OVER_MAX_CLASS =
  "bg-destructive/[0.08] shadow-[inset_0_0_0_1px_color-mix(in_srgb,var(--color-destructive)_40%,transparent)] hover:bg-destructive/[0.13]";

export interface RateLineWeightCapInput {
  max_weight?: number | null;
  overweight_tier_max_kg?: number | null;
  overweight_policies?: OverweightPolicy[] | null;
  equipment_size?: string | null;
  equipment_type?: string | null;
  origin_locode?: string | null;
}

function finiteKg(value: number | null | undefined): number | null {
  return value != null && Number.isFinite(value) && value > 0 ? value : null;
}

/**
 * Highest closed overweight-tier ceiling (kg) for this line. An open-ended
 * last step (`max_kg` null) means the policy has no finite tier cap.
 */
export function maxOverweightTierKg(input: RateLineWeightCapInput): number | null {
  const policies = input.overweight_policies ?? [];
  const applicable = filterApplicableOverweightPolicies(
    policies,
    input.equipment_size,
    input.equipment_type,
    input.origin_locode,
  );
  let highest: number | null = null;
  let openEnded = false;
  for (const policy of applicable) {
    for (const step of policy.steps) {
      if (step.max_kg == null) {
        openEnded = true;
        continue;
      }
      if (highest == null || step.max_kg > highest) highest = step.max_kg;
    }
  }
  if (openEnded) return null;
  return finiteKg(highest) ?? finiteKg(input.overweight_tier_max_kg);
}

/** `COALESCE(max_weight, max_tier_weight)` — the hard per-line cap. */
export function coalescedMaxWeightKg(input: RateLineWeightCapInput): number | null {
  return finiteKg(input.max_weight) ?? maxOverweightTierKg(input);
}

export interface RateLineWeightExceeds {
  exceeds: boolean;
  grossKg: number;
  capKg: number;
}

/** True when lookup gross is over `COALESCE(max_weight, max_tier_weight)`. */
export function rateLineGrossExceedsCap(
  input: RateLineWeightCapInput & { gross_kg?: number | null },
): RateLineWeightExceeds | null {
  const grossKg = finiteKg(input.gross_kg);
  const capKg = coalescedMaxWeightKg(input);
  if (grossKg == null || capKg == null) return null;
  return { exceeds: grossKg > capKg, grossKg, capKg };
}

export function rateLineRowWeightCapInput(
  line: RateLineRow,
): RateLineWeightCapInput {
  return {
    max_weight: line.max_weight,
    overweight_policies: line.cardPolicies?.overweight_policies,
    equipment_size: line.equipment_size,
    equipment_type: line.equipment_type,
    origin_locode: line.origin_port.locode,
  };
}

/** Lines within the weight cap first; preserve relative order via `secondaryCompare`. */
export function sortWithWeightCapFirst<T>(
  items: readonly T[],
  opts: {
    isExceeded: (item: T) => boolean;
    secondaryCompare: (a: T, b: T) => number;
  },
): T[] {
  return [...items].sort((a, b) => {
    const aOver = opts.isExceeded(a);
    const bOver = opts.isExceeded(b);
    if (aOver !== bOver) return aOver ? 1 : -1;
    return opts.secondaryCompare(a, b);
  });
}
